import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { genres } from "@/mocks/genres";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影分类", to: "/category/action", dropdown: true },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/search") return location.pathname === "/search";
    if (to === "/about") return location.pathname === "/about";
    return location.pathname.startsWith("/category");
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "border-b border-background-200/70 bg-background-50/95 backdrop-blur"
          : "border-b border-transparent bg-gradient-to-b from-background-950/80 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-4 px-4 md:h-20 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-movie-2-line text-xl"></i>
          </span>
          <span className="font-heading text-lg font-extrabold tracking-wide text-foreground-950 md:text-xl">
            鸭王<span className="text-accent-500">在线观看</span>
          </span>
        </Link>

        <nav className="ml-2 hidden items-center gap-1 md:flex">
          {navItems.map((item) =>
            item.dropdown ? (
              <div
                key={item.label}
                ref={dropdownRef}
                className="relative"
              >
                <button
                  type="button"
                  onClick={() => setCategoryOpen((v) => !v)}
                  className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive(item.to)
                      ? "text-accent-500"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                  <i
                    className={`ri-arrow-down-s-line text-base transition-transform ${
                      categoryOpen ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>
                {categoryOpen && (
                  <div className="absolute left-0 top-full mt-2 w-[420px] animate-fade-in rounded-lg border border-background-200/70 bg-background-100 p-3 shadow-xl">
                    <div className="grid grid-cols-3 gap-1">
                      {genres.map((g) => (
                        <Link
                          key={g.slug}
                          to={`/category/${g.slug}`}
                          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-500/15 hover:text-foreground-950"
                        >
                          <i className={`${g.icon} text-base text-primary-300`}></i>
                          {g.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive(item.to)
                    ? "text-accent-500"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden h-10 items-center gap-2 rounded-full border border-background-300/60 bg-background-100 px-4 lg:flex"
        >
          <i className="ri-search-line text-base text-foreground-500"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索影片、类型、地区"
            className="w-44 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
          />
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-950"
          >
            搜索
          </button>
        </form>

        <Link
          to="/search"
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300/60 text-foreground-700 lg:hidden"
          aria-label="搜索"
        >
          <i className="ri-search-line text-lg"></i>
        </Link>

        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300/60 text-foreground-950 md:hidden"
          aria-label="打开菜单"
        >
          <i className="ri-menu-line text-xl"></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          ></div>
          <div className="absolute left-0 top-0 h-full w-[78%] max-w-[320px] animate-fade-in overflow-y-auto bg-background-100 p-5">
            <div className="mb-6 flex items-center justify-between">
              <span className="font-heading text-lg font-extrabold text-foreground-950">
                鸭王<span className="text-accent-500">在线观看</span>
              </span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300/60 text-foreground-950"
                aria-label="关闭菜单"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form onSubmit={submitSearch} className="mb-5 flex h-11 items-center gap-2 rounded-full border border-background-300/60 bg-background-50 px-4">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片"
                className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
              />
            </form>

            <nav className="flex flex-col gap-1">
              {[
                { label: "首页", to: "/" },
                { label: "电影分类", to: "/category/action" },
                { label: "搜索", to: "/search" },
                { label: "关于", to: "/about" },
              ].map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`flex items-center gap-3 rounded-md px-3 py-3 text-base ${
                    isActive(item.to)
                      ? "bg-primary-500/15 text-accent-500"
                      : "text-foreground-700"
                  }`}
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                  {item.label}
                </Link>
              ))}
            </nav>

            <p className="mb-3 mt-6 text-xs font-medium uppercase tracking-wider text-foreground-500">
              热门类型
            </p>
            <div className="grid grid-cols-2 gap-2">
              {genres.slice(0, 8).map((g) => (
                <Link
                  key={g.slug}
                  to={`/category/${g.slug}`}
                  className="flex items-center gap-2 rounded-md border border-background-300/50 px-3 py-2 text-sm text-foreground-700"
                >
                  <i className={`${g.icon} text-base text-primary-300`}></i>
                  {g.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}